import type { GitDependencyReference } from "@octopusdeploy/octopus-server-client";
import type { GitSourceReference } from "@octopusdeploy/step-inputs";
import type { InputProperty, PathToInput, RootInputSchema, ObjectRuntimeInputs, NonDiscriminatorTypeDefinition, PlainObjectTypeDefinition } from "@octopusdeploy/step-runtime-inputs";
import { exhaustiveCheck, createInputValueAccessor, createPathToInput, isBoundValue } from "@octopusdeploy/step-runtime-inputs";
import { convertToRuntimeGitSourceSelection } from "./Inputs/Components/GitSourceSelector/GitSourceSelectionConverters";

export function getGitDependencyFromInputs<StepInputs>(inputs: ObjectRuntimeInputs<StepInputs>, schema: RootInputSchema, existingGitDependencies: GitDependencyReference[]): GitDependencyReference[] {
    const inputObjectSchema: PlainObjectTypeDefinition = { type: "object", nonDiscriminatorProperties: schema.properties, discriminatorProperties: [] };

    return getPackageReferences(inputObjectSchema, []);

    function getPackageReferences(typeDefinition: NonDiscriminatorTypeDefinition, path: PathToInput): GitDependencyReference[] {
        switch (typeDefinition.type) {
            case "account":
            case "string":
            case "primitive":
            case "sensitive":
            case "package":
            case "container-image":
                return [];
            case "object":
                return typeDefinition.nonDiscriminatorProperties.reduce(function (acc: GitDependencyReference[], property: InputProperty): GitDependencyReference[] {
                    return [...acc, ...getPackageReferences(property.type, [...path, property.name])];
                }, []);
            case "array":
                return typeDefinition.itemTypes.reduce(function (acc: GitDependencyReference[], property: NonDiscriminatorTypeDefinition, i: number): GitDependencyReference[] {
                    return [...acc, ...getPackageReferences(property, [...path, i])];
                }, []);

            case "git-source": {
                const pathToInput = createPathToInput<GitSourceReference>(path);
                const inputAccessor = createInputValueAccessor<StepInputs, GitSourceReference>(pathToInput);
                const inputValue = inputAccessor.getInputValue(inputs);
                if (isBoundValue(inputValue)) {
                    throw new Error("Bound values for Git Sources are not yet supported");
                }
                const runtimeGitSourceSelection = convertToRuntimeGitSourceSelection(inputValue);
                if (runtimeGitSourceSelection.type !== undefined && runtimeGitSourceSelection.type !== "external") {
                    throw new Error("Git source selection is not external");
                }
                return [
                    {
                        Name: "",
                        RepositoryUri: runtimeGitSourceSelection.url,
                        DefaultBranch: runtimeGitSourceSelection.defaultBranch,
                        GitCredentialId: runtimeGitSourceSelection.gitCredential,
                        GitCredentialType: runtimeGitSourceSelection.isAnonymous ? "Anonymous" : "Library",
                        StepPackageInputsReferenceId: runtimeGitSourceSelection.referenceId,
                        FilePathFilters: [],
                    },
                ];
            }
        }

        exhaustiveCheck(typeDefinition, "One or more of the typeDefinition cases were not handled");
        return [];
    }
}
